import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBlob, ref } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { createImageLoader } from '../lib/imageLoader';
import { withReadTimeout } from '../lib/readLoading';
import type { Job } from '../types/models';

/**
 * A few evidence thumbnails, for places that decide something about a job from its
 * photographs (the accounts queue prices from them). Reads go through the Storage SDK
 * so tenant rules are enforced; a thumbnail that will not load is simply left out
 * rather than reported as missing evidence — only the server can say that.
 */
export function EvidenceStrip({
  job, max = 4, size = 46, href,
}: {
  job: Job;
  max?: number;
  size?: number;
  /** Where a thumbnail takes you — normally the job the evidence belongs to. */
  href?: string;
}) {
  const paths = useMemo(() => {
    const images = job.typedImages ?? [];
    return images
      .map((image) => image.thumbnailPath || image.storagePath)
      .filter((path): path is string => !!path);
  }, [job.typedImages]);

  const total = job.imageCount ?? job.typedImages?.length ?? job.imageUrls?.length ?? 0;
  const shown = paths.slice(0, max);
  const overflow = total - shown.length;
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (shown.length === 0) return;
    const loader = createImageLoader((path) => withReadTimeout(getBlob(ref(storage, path), 8 * 1024 * 1024), 20_000), 2);
    let cancelled = false;
    const created: string[] = [];
    void Promise.all(shown.map(async (path) => {
      try {
        const blob = await loader.load(path);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        created.push(url);
        setUrls((current) => ({ ...current, [path]: url }));
      } catch (error) {
        console.warn('Evidence thumbnail unavailable', error);
      }
    }));
    return () => {
      cancelled = true;
      loader.clear();
      created.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown.join('|')]);

  if (total === 0) {
    return <p className="text-[13px] text-ink3">No photographs.</p>;
  }

  const tile = 'block overflow-hidden rounded-[9px] bg-sunken';
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label={`${total} photograph${total === 1 ? '' : 's'}`}>
      {shown.map((path, index) => {
        const image = urls[path] && <img src={urls[path]} alt="" loading="lazy" className="h-full w-full object-cover" />;
        return href ? (
          <Link key={path} to={href} aria-label={`Open ${job.registrationNumber || 'this job'}, photograph ${index + 1}`}
            className={`${tile} transition-opacity duration-[180ms] ease-out hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
            style={{ width: size, height: size }}>
            {image}
          </Link>
        ) : (
          <span key={path} className={tile} style={{ width: size, height: size }}>{image}</span>
        );
      })}
      {overflow > 0 && (
        href ? (
          <Link to={href} aria-label={`Open ${job.registrationNumber || 'this job'} to see all ${total} photographs`}
            className="flex items-center justify-center rounded-[9px] bg-sunken font-mono text-[12.5px] font-semibold text-ink3 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            style={{ width: size, height: size }}>
            +{overflow}
          </Link>
        ) : (
          <span className="flex items-center justify-center rounded-[9px] bg-sunken font-mono text-[12.5px] font-semibold text-ink3"
            style={{ width: size, height: size }}>
            +{overflow}
          </span>
        )
      )}
    </div>
  );
}
